import { useState, forwardRef, useCallback } from 'react';
import type { CustomContentProps } from 'notistack';
import { useSnackbar, SnackbarContent } from 'notistack';
import { Avatar, Box, Card, CardActions, Collapse, IconButton, Paper, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ObjectToTable from '@components/ObjectToTable/ObjectToTable';
import HighlightedActionText from '@components/HighlightedActionText/HighlightedActionText';
import { useUser } from 'reactfire';
import colors from '@phyllome/helpers/colors';

interface ReportCompleteProps extends CustomContentProps {
  allowDownload?: boolean;
  meta?: any;
  thumbnail?: string;
  user?: string;
}

const StreamMessageSnackbar = forwardRef<HTMLDivElement, ReportCompleteProps>(
  ({ id, ...props }, ref) => {
    const user = useUser();

    const { closeSnackbar } = useSnackbar();
    const [expanded, setExpanded] = useState(false);

    const handleExpandClick = useCallback(() => {
      setExpanded((oldExpanded) => !oldExpanded);
    }, []);

    const handleDismiss = useCallback(() => {
      closeSnackbar(id);
    }, [id, closeSnackbar]);

    const showExpansion = props.meta !== undefined;
    const isCurrentUserSnackbar = props.user === user.data?.email;

    return (
      <SnackbarContent ref={ref} style={{ minWidth: '400px' }}>
        <Stack direction='row' spacing={2} sx={{ minWidth: '400px' }}>
          <Box sx={{ paddingTop: 1 }}>
            <Avatar src={props.thumbnail} alt='User Thumbnail' />
          </Box>
          <Card sx={(theme) => ({
            backgroundColor: isCurrentUserSnackbar
              ? (theme.palette.mode === 'dark' ? '#7a6418' : '#fddc6c')
              : (theme.palette.mode === 'dark' ? theme.palette.background.paper : colors.fog),
            color: theme.palette.text.primary,
            width: '100%',
          })}>
            <CardActions sx={{ padding: '8px 8px 8px 16px', justifyContent: 'space-between' }}>
              <Typography variant='body1'>
                <HighlightedActionText text={props.message?.toString() || 'Message Error'} />
              </Typography>
              <Box sx={{ marginLeft: 'auto' }}>
                {showExpansion && (
                  <IconButton
                    aria-label='Show more'
                    size='small'
                    sx={{
                      padding: '8px 8px',
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      color: 'inherit',
                      transition: 'all .2s',
                    }}
                    onClick={handleExpandClick}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                )}
                <IconButton
                  size='small'
                  sx={{
                    padding: '8px 8px',
                    color: 'inherit',
                    transition: 'all .2s',
                  }}
                  onClick={handleDismiss}
                >
                  <CloseIcon fontSize='small' />
                </IconButton>
              </Box>
            </CardActions>
            <Collapse in={expanded} timeout='auto' unmountOnExit>
              <Paper sx={(theme) => ({
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 0,
                padding: 1,
              })}>
                {typeof props.meta === 'object' && props.meta !== null ? (
                  <ObjectToTable data={props.meta} />
                ) : (
                  <div>{props.meta}</div>
                )}
              </Paper>
            </Collapse>
          </Card>
        </Stack>
      </SnackbarContent>
    );
  },
);

StreamMessageSnackbar.displayName = 'StreamMessageSnackbar';

export default StreamMessageSnackbar;
